import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import swal from "sweetalert";
import { formatCuit } from "../pago_proveedores/utils";
import {
  emailListoParaEnvio,
  enviarNotificacionEstadoProveedor,
  esEmailValido,
  helperTextCorreoAlta,
  obtenerEmailMaestro,
  resolverEmailParaEnvio,
} from "./altaProveedores.correo";
import CampoCorreoProveedor from "./CampoCorreoProveedor";
import CustomBackdrop from "../../CustomBackdrop";
import {
  getProveedorByCuit,
  normalizarCuit,
  signUpCuit,
} from "./altaProveedores.service";

export default function ModalAltaProveedor({ open, onClose, onSuccess }) {
  const [cuit, setCuit] = useState("");
  const [razonSocial, setRazonSocial] = useState("");
  const [emailMaestro, setEmailMaestro] = useState("");
  const [emailManual, setEmailManual] = useState("");
  const [busquedaRealizada, setBusquedaRealizada] = useState(false);
  const [proveedorEncontrado, setProveedorEncontrado] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [creando, setCreando] = useState(false);
  const [mensajeCreacion, setMensajeCreacion] = useState("");
  const [errorBusqueda, setErrorBusqueda] = useState("");
  const [errorEmail, setErrorEmail] = useState("");

  const cuitNorm = normalizarCuit(cuit);
  const cuitValido = cuitNorm.length === 11;
  const loading = buscando || creando;
  const emailParaEnvio = resolverEmailParaEnvio(emailMaestro, emailManual);
  const requiereEmailManual = !emailMaestro;
  const emailListo = emailListoParaEnvio(emailMaestro, emailManual);

  const resetForm = () => {
    setCuit("");
    setRazonSocial("");
    setEmailMaestro("");
    setEmailManual("");
    setBusquedaRealizada(false);
    setProveedorEncontrado(false);
    setErrorBusqueda("");
    setErrorEmail("");
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleBuscar = async (e) => {
    e?.preventDefault?.();
    if (!cuitValido) return;

    setBuscando(true);
    setErrorBusqueda("");
    setBusquedaRealizada(false);
    setProveedorEncontrado(false);
    setRazonSocial("");
    setEmailMaestro("");
    setEmailManual("");
    setErrorEmail("");

    try {
      const proveedor = await getProveedorByCuit(cuitNorm);
      setBusquedaRealizada(true);
      if (proveedor) {
        setProveedorEncontrado(true);
        setRazonSocial(
          proveedor.razon_social ||
            proveedor.denominacion ||
            proveedor.nombre_fantasia ||
            ""
        );
        const correoProveedor = obtenerEmailMaestro(proveedor);
        if (correoProveedor) {
          setEmailMaestro(correoProveedor);
        }
      }
    } catch (err) {
      setErrorBusqueda(err.message || "Error al buscar el proveedor");
    } finally {
      setBuscando(false);
    }
  };

  const handleCrear = async () => {
    if (!cuitValido || !emailListo) {
      if (requiereEmailManual && !esEmailValido(emailManual)) {
        setErrorEmail("Ingrese un correo válido para notificar al proveedor");
      }
      return;
    }

    setCreando(true);
    setMensajeCreacion("Creando acceso al portal...");
    try {
      const resultado = await signUpCuit(cuitNorm, 1, emailParaEnvio);
      const nombreProveedor = resultado.razon_social || razonSocial || "";
      setMensajeCreacion("Enviando correo al proveedor...");
      await enviarNotificacionEstadoProveedor({
        email: emailParaEnvio,
        cuit: cuitNorm,
        razonSocial: nombreProveedor,
        tipo: "alta-inicial",
        setupToken: resultado.setupToken,
      });
      await swal({
        title: "Alta exitosa",
        text:
          resultado.msg ||
          `Se creó el acceso al portal para el CUIT ${formatCuit(cuitNorm)}.`,
        icon: "success",
        closeOnClickOutside: false,
      });
      onSuccess?.({
        cuit: cuitNorm,
        activo: 1,
        razon_social: resultado.razon_social || razonSocial || "",
      });
      onClose();
    } catch (err) {
      swal({
        title: "No se pudo dar de alta",
        text: err.message || "Ocurrió un error al crear el acceso",
        icon: "error",
        closeOnClickOutside: false,
      });
    } finally {
      setCreando(false);
      setMensajeCreacion("");
    }
  };

  return (
    <>
      <CustomBackdrop open={creando} text={mensajeCreacion} />
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={creando}
      >
      <DialogTitle
        sx={{
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        Dar de alta proveedor
        <IconButton
          aria-label="cerrar"
          onClick={handleClose}
          disabled={loading}
          edge="end"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack
          component="form"
          onSubmit={handleBuscar}
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "flex-start" }}
          sx={{ mt: 1 }}
        >
          <TextField
            fullWidth
            label="CUIT del proveedor"
            placeholder="Ej: 30-64708229-3"
            value={cuit}
            onChange={(e) => {
              setCuit(e.target.value);
              setBusquedaRealizada(false);
              setProveedorEncontrado(false);
              setRazonSocial("");
              setEmailMaestro("");
              setEmailManual("");
              setErrorBusqueda("");
              setErrorEmail("");
            }}
            disabled={loading}
            inputProps={{ inputMode: "numeric" }}
          />
          <Button
            type="submit"
            variant="outlined"
            startIcon={buscando ? <CircularProgress size={18} /> : <SearchIcon />}
            disabled={!cuitValido || loading}
            sx={{ minWidth: { sm: 120 }, height: 56 }}
          >
            Buscar
          </Button>
        </Stack>

        {errorBusqueda && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorBusqueda}
          </Alert>
        )}

        {busquedaRealizada && proveedorEncontrado && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Proveedor encontrado:{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {razonSocial || "Sin razón social"}
            </Box>
            {emailMaestro ? (
              <>
                <br />
                Se notificará al correo registrado:{" "}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {emailMaestro}
                </Box>
              </>
            ) : null}
          </Alert>
        )}

        {busquedaRealizada && !proveedorEncontrado && !errorBusqueda && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Proveedor no encontrado en el maestro de proveedores. Puede crear un
            acceso al portal igualmente.
          </Alert>
        )}

        {cuitValido && requiereEmailManual && (
          <CampoCorreoProveedor
            emailMaestro={emailMaestro}
            emailManual={emailManual}
            onEmailManualChange={(valor) => {
              setEmailManual(valor);
              setErrorEmail("");
            }}
            errorEmail={errorEmail}
            disabled={loading}
            helperText={helperTextCorreoAlta}
            sx={{ mt: 2 }}
          />
        )}

        {cuitValido && (
          <Box sx={{ mt: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Se creará el acceso al portal para el CUIT{" "}
              <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                {formatCuit(cuitNorm)}
              </Box>
              {razonSocial ? (
                <>
                  {" "}
                  ({razonSocial})
                </>
              ) : null}
              .
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleCrear}
          disabled={!cuitValido || !emailListo || loading}
          startIcon={creando ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {creando ? "Procesando..." : "Crear"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
